import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Check, Instagram } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { scrollToId } from "./Header";
import { SERVICES, CONTACT, waLink, MAPS_LINK, srcSetFor } from "@/lib/content";
import { PROFILE_OPTIONS, PROFILE_FROM_PARAM } from "@/lib/profilesData";
import { trackContactForm } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { name: "", email: "", phone: "", service: "", location: "", message: "", profile: "", consent: false };

const Field = ({ label, id, children }) => (
  <label htmlFor={id} className="group block border-b border-[#1A2218]/15 pb-2 transition-colors duration-300 focus-within:border-[#5D995E]">
    <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#4A5549]">{label}</span>
    {children}
  </label>
);

const inputCls = "mt-2 w-full bg-transparent text-base text-[#1A2218] outline-none placeholder:text-[#4A5549]/60 sm:text-lg";

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const location = useLocation();

  // Pré-seleciona "Você é" quando a pessoa chega de uma página de perfil (ex.: /para-advogados)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const perfil = params.get("perfil");
    if (!perfil || !PROFILE_FROM_PARAM[perfil]) return;
    setForm((f) => ({ ...f, profile: PROFILE_FROM_PARAM[perfil] }));
    const t = setTimeout(() => scrollToId("contato"), 400);
    return () => clearTimeout(t);
  }, [location.search]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!API) return toast.error("Envio indisponível no momento. Fale conosco pelo WhatsApp.");
    if (!form.profile) return toast.error('Selecione uma opção em "Você é".');
    if (!form.consent) return toast.error("É preciso aceitar a Política de Privacidade.");
    trackContactForm(window.location.pathname);
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, website: e.currentTarget.website?.value || "" });
      setSent(true);
      setForm(EMPTY);
      toast.success("Mensagem enviada! Retornaremos em breve.");
    } catch (err) {
      toast.error("Não foi possível enviar. Verifique os dados ou fale pelo WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" data-testid="contact-section" className="mx-auto max-w-[1400px] px-5 py-8 sm:px-8 sm:py-14 md:py-36">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal><Overline>Contato</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight text-[#1A2218] sm:mt-8 sm:text-5xl lg:text-6xl">
              Vamos conversar sobre a sua <em className="text-[#5D995E]">propriedade?</em>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#4A5549] sm:mt-8 md:text-lg">
              Conte o que você precisa. Nossa equipe retorna com orientação e os próximos passos.
            </p>
            <div className="mt-8 space-y-3 sm:mt-10">
              <a data-testid="contact-phone-link" href={`tel:+${CONTACT.wa}`} className="link-underline block font-serif text-2xl text-[#1A2218] sm:text-3xl">{CONTACT.phone}</a>
              <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="link-underline block break-all text-lg text-[#1A2218]">{CONTACT.email}</a>
              <a data-testid="contact-address-link" href={MAPS_LINK} target="_blank" rel="noreferrer" className="link-underline inline-block text-[#4A5549] underline decoration-[#4A5549]/40 underline-offset-4">{CONTACT.address} · {CONTACT.city} · {CONTACT.cep}</a>
              <p data-testid="contact-hours" className="text-[#4A5549]">{CONTACT.hours}</p>
              <a data-testid="contact-instagram" href={CONTACT.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-medium text-[#233D25]">
                <Instagram className="h-5 w-5" /> {CONTACT.instagramHandle}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.3} className="mt-10 hidden overflow-hidden rounded-[24px] rounded-bl-[100px] lg:block">
            <img src="/img/rice.webp" alt="Arrozais irrigados ao pôr do sol" srcSet={srcSetFor("/img/rice.webp")} sizes="(min-width:768px) 45vw, calc(100vw - 40px)" width={1600} height={1067} loading="lazy" className="aspect-[16/10] w-full object-cover" />
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
          <form data-testid="contact-form" onSubmit={submit} className="rounded-[28px] border border-[#E5E0D5] bg-white p-4 shadow-[0_30px_80px_-40px_rgba(31,58,36,0.35)] sm:p-10">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

            <div className="grid grid-cols-2 gap-4 sm:gap-8">
              <Field label="Nome *" id="c-name">
                <input id="c-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder="Seu nome" />
              </Field>
              <Field label="WhatsApp *" id="c-phone">
                <input id="c-phone" data-testid="contact-phone-input" required minLength={8} value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(53) 99999-0000" />
              </Field>
              <Field label="E-mail (opcional)" id="c-email">
                <input id="c-email" type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} className={inputCls} placeholder="voce@email.com" />
              </Field>
              <Field label="Município" id="c-loc">
                <input id="c-loc" data-testid="contact-location-input" value={form.location} onChange={set("location")} className={inputCls} placeholder="Cidade / RS" />
              </Field>
            </div>
            <div className="mt-6 sm:mt-8">
              <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#4A5549]">Você é: *</p>
              <div className="mt-3 sm:mt-4">
                <select
                  data-testid="contact-profile-select"
                  required
                  value={form.profile}
                  onChange={(e) => setForm((f) => ({ ...f, profile: e.target.value }))}
                  className="w-full appearance-none rounded-xl border border-[#E5E0D5] bg-white px-4 py-2.5 text-base text-[#1A2218] outline-none transition-colors duration-300 focus:border-[#5D995E] md:hidden"
                >
                  <option value="" disabled>Selecione uma opção</option>
                  {PROFILE_OPTIONS.map((p) => (
                    <option key={p.slug} value={p.label}>{p.label}</option>
                  ))}
                </select>
                <div className="hidden flex-wrap gap-2 md:flex" role="radiogroup" aria-label="Você é">
                  {PROFILE_OPTIONS.map((p) => (
                    <button
                      type="button"
                      key={p.slug}
                      role="radio"
                      aria-checked={form.profile === p.label}
                      data-testid={`contact-profile-${p.slug}`}
                      onClick={() => setForm((f) => ({ ...f, profile: f.profile === p.label ? "" : p.label }))}
                      className={`rounded-full border px-4 py-2 text-sm transition-[background-color,color,border-color] duration-300 ${
                        form.profile === p.label ? "border-[#1F3A24] bg-[#233D25] text-[#F9F8F3]" : "border-[#E5E0D5] text-[#1A2218] hover:border-[#1F3A24]"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-6 sm:mt-8">
              <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#4A5549]">Serviço de interesse</p>
              <div className="mt-3 sm:mt-4">
                <select
                  data-testid="contact-service-select"
                  value={form.service}
                  onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
                  className="w-full appearance-none rounded-xl border border-[#E5E0D5] bg-white px-4 py-2.5 text-base text-[#1A2218] outline-none transition-colors duration-300 focus:border-[#5D995E] md:hidden"
                >
                  <option value="">Selecione um serviço (opcional)</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>{s.title}</option>
                  ))}
                  <option value="Outro / não sei">Outro / não sei</option>
                </select>
                <div className="hidden flex-wrap gap-2 md:flex">
                  {SERVICES.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      data-testid={`contact-service-${s.id}`}
                      onClick={() => setForm((f) => ({ ...f, service: f.service === s.title ? "" : s.title }))}
                      className={`rounded-full border px-4 py-2 text-sm transition-[background-color,color,border-color] duration-300 ${
                        form.service === s.title ? "border-[#233D25] bg-[#233D25] text-[#F9F8F3]" : "border-[#E5E0D5] text-[#1A2218] hover:border-[#233D25]"
                      }`}
                    >
                      {s.title}
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid="contact-service-outro"
                    onClick={() => setForm((f) => ({ ...f, service: f.service === "Outro / não sei" ? "" : "Outro / não sei" }))}
                    className={`rounded-full border px-4 py-2 text-sm transition-[background-color,color,border-color] duration-300 ${
                      form.service === "Outro / não sei" ? "border-[#233D25] bg-[#233D25] text-[#F9F8F3]" : "border-[#E5E0D5] text-[#1A2218] hover:border-[#233D25]"
                    }`}
                  >
                    Outro / não sei
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-6 sm:mt-8">
              <Field label="Mensagem *" id="c-msg">
                <textarea id="c-msg" data-testid="contact-message-input" required minLength={5} rows={3} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Conte brevemente sua necessidade" />
              </Field>
            </div>
            <label className="mt-6 flex items-start gap-3 text-sm text-[#4A5549] sm:mt-8">
              <input
                type="checkbox"
                required
                checked={form.consent}
                onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                data-testid="contact-consent"
                className="mt-0.5 h-4 w-4 accent-[#233D25]"
              />
              <span>
                Autorizo o contato da Melo Agro Engenharia e li a{" "}
                <Link to="/politica-de-privacidade" data-testid="contact-privacy-link" className="font-semibold text-[#233D25] underline underline-offset-2">Política de Privacidade</Link>. *
              </span>
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="group mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#233D25] px-8 py-5 font-semibold text-[#F9F8F3] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#5D995E] disabled:opacity-70 sm:w-auto"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : sent ? <Check className="h-5 w-5" /> : null}
              {loading ? "Enviando..." : "Enviar mensagem"}
              {!loading && <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />}
            </button>
            {sent && (
              <p data-testid="contact-success-message" className="mt-5 text-sm font-medium text-[#233D25]">
                Recebemos sua mensagem! Retornamos em até 1 dia útil pelo WhatsApp.
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};
